import { HttpErrorResponse } from '@angular/common/http';

interface SentryFilterEvent {
  message?: string;
  transaction?: string;
  exception?: {
    values?: {
      value?: string;
      type?: string;
    }[];
  };
}

interface SentryFilterHint {
  originalException?: unknown;
}

const OAUTH_TOKEN_400_PATTERN = /openid-connect\/token.*400\s+bad request/i;
const LOGIN_NETWORK_ERROR_PATTERN = /^(load failed|failed to fetch)$/i;
const LOGIN_ROUTE_PATTERN = /^\/login\/?$/;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof HttpErrorResponse) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return '';
};

const isHttpLikeError = (error: unknown): error is { status?: number; url?: string } => {
  return typeof error === 'object' && error !== null && ('status' in error || 'url' in error);
};

const collectEventMessages = (event: SentryFilterEvent): string[] => {
  const messages: string[] = [];

  if (event.message) {
    messages.push(event.message);
  }

  for (const exception of event.exception?.values ?? []) {
    if (exception.value) {
      messages.push(exception.value);
    }
  }

  return messages;
};

export const isExpectedOAuthTokenError = (error: unknown): boolean => {
  if (error instanceof HttpErrorResponse) {
    return error.status === 400 && (error.url?.includes('openid-connect/token') ?? false);
  }

  if (isHttpLikeError(error)) {
    return error.status === 400 && (error.url?.includes('openid-connect/token') ?? false);
  }

  const message = getErrorMessage(error);
  return OAUTH_TOKEN_400_PATTERN.test(message);
};

export const shouldDropAngularError = (error: unknown): boolean => {
  if (isExpectedOAuthTokenError(error)) {
    return true;
  }

  const message = getErrorMessage(error);
  if (LOGIN_NETWORK_ERROR_PATTERN.test(message)) {
    return true;
  }

  return false;
};

export const shouldDropSentryEvent = (event: SentryFilterEvent, hint?: SentryFilterHint): boolean => {
  if (shouldDropAngularError(hint?.originalException)) {
    return true;
  }

  const messages = collectEventMessages(event);
  if (messages.some(message => OAUTH_TOKEN_400_PATTERN.test(message))) {
    return true;
  }

  const transaction = event.transaction ?? '';
  if (LOGIN_ROUTE_PATTERN.test(transaction) && messages.some(message => LOGIN_NETWORK_ERROR_PATTERN.test(message))) {
    return true;
  }

  return false;
};
